/**
 * Fetches all items from a paginated GraphQL query
 */

export interface GraphQLClient {
  graphql: (options: { query: string; variables?: Record<string, any> }) => Promise<GraphQLResponse>
}

export interface GraphQLResponse {
  data?: Record<string, any>
  errors?: GraphQLError[]
}

export interface GraphQLError {
  message: string
  locations?: Array<{ line: number; column: number }>
  path?: Array<string | number>
}

export interface PaginatedData {
  items: any[]
  nextToken?: string | null
}

/**
 * Fetches all items from a paginated GraphQL query
 * @param client - GraphQL client instance
 * @param query - GraphQL query string
 * @param variables - Query variables (excluding pagination)
 * @param dataPath - Path to the data in response (e.g., 'listORIGIN_PRODUCTS')
 * @returns All items from all pages
 */
export const fetchAllPages = async (
  client: GraphQLClient,
  query: string,
  variables: Record<string, any> = {},
  dataPath: string
): Promise<any[]> => {
  try {
    // Validate query is not null/undefined
    if (!query) {
      console.error('fetchAllPages: query is null or undefined for dataPath:', dataPath)
      return []
    }
    
    let allItems: any[] = []
    let nextToken: string | null = null
    let hasMorePages = true
    
    while (hasMorePages) {
      // Add pagination variables that VTL templates expect
      const queryVariables: Record<string, any> = {
        ...variables,
        limit: 1000, // Use high limit per page
        nextToken: nextToken
      }
      
      const result: GraphQLResponse = await client.graphql({ 
        query, 
        variables: queryVariables 
      })
      
      if (result.errors && result.errors.length > 0) {
        console.error('GraphQL errors:', result.errors)
        result.errors.forEach((error) => {
          console.error('Error message:', error.message)
          console.error('Error locations:', error.locations)
          console.error('Error path:', error.path)
        })
        break
      }
      
      const data = result.data?.[dataPath]
      let items: any[] = []
      
      if (Array.isArray(data)) {
        items = data
        hasMorePages = false // No pagination info available
      } else if (data?.items && Array.isArray(data.items)) {
        items = data.items
        const newNextToken: string | null = data.nextToken || null
        
        // Safety check to prevent infinite loops with same nextToken
        if (newNextToken && newNextToken === nextToken) {
          console.warn('fetchAllPages: Detected infinite loop with same nextToken:', newNextToken)
          break
        }
        
        // Safety check to prevent infinite loops with numeric nextToken that doesn't advance
        if (newNextToken && nextToken && 
            !isNaN(Number(newNextToken)) && !isNaN(Number(nextToken)) && 
            parseInt(newNextToken) <= parseInt(nextToken)) {
          console.warn('fetchAllPages: Detected infinite loop with non-advancing nextToken:', newNextToken)
          break
        }
        
        nextToken = newNextToken
        hasMorePages = !!nextToken
      } else {
        hasMorePages = false
      }
      
      allItems = allItems.concat(items)
      
      // Safety check to prevent infinite loops
      if (allItems.length > 50000) {
        console.warn('fetchAllPages: Reached safety limit of 50,000 records')
        break
      }
    }
    
    return allItems
  } catch (error) {
    console.error('Error fetching data:', error)
    if (error && typeof error === 'object' && 'errors' in error) {
      const graphqlError = error as { errors: GraphQLError[] }
      graphqlError.errors.forEach((err) => {
        console.error('Detailed error:', err.message, err.locations, err.path)
      })
    }
    return []
  }
}
